import { salesPointConfigurationSelector } from '@/features/configuration/configurationSelector';
import { useSelector } from '@/store/utils';
import type { PersonDataFieldDTO } from '@/types/dto/agent';
import { useMemo } from 'react';

export enum PASSENGER_DATA_FIELD {
  FIRST_NAME = 'PASSENGER_DATA_FIELD.FIRST_NAME',
  LAST_NAME = 'PASSENGER_DATA_FIELD.LAST_NAME',
  EMAIL = 'PASSENGER_DATA_FIELD.EMAIL',
  PHONE = 'PASSENGER_DATA_FIELD.PHONE',
  DATE_OF_BIRTH = 'PASSENGER_DATA_FIELD.DATE_OF_BIRTH',
  GENDER = 'PASSENGER_DATA_FIELD.GENDER',
}

export enum PURCHASER_DATA_FIELD {
  FIRST_NAME = 'PURCHASER_DATA_FIELD.FIRST_NAME',
  LAST_NAME = 'PURCHASER_DATA_FIELD.LAST_NAME',
  EMAIL = 'PURCHASER_DATA_FIELD.EMAIL',
  PHONE = 'PURCHASER_DATA_FIELD.PHONE',
}

export enum VISIBILITY_OPTION {
  DISPLAYED = 'VISIBILITY_OPTION.DISPLAYED',
  MANDATORY = 'VISIBILITY_OPTION.MANDATORY',
  NOT_DISPLAYED = 'VISIBILITY_OPTION.NOT_DISPLAYED',
}

export const PASSENGER_FIELD_NAME_MAP: Record<
  PASSENGER_DATA_FIELD,
  'firstName' | 'lastName' | 'email' | 'phoneNumber' | 'birthDate' | 'gender'
> = {
  [PASSENGER_DATA_FIELD.FIRST_NAME]: 'firstName',
  [PASSENGER_DATA_FIELD.LAST_NAME]: 'lastName',
  [PASSENGER_DATA_FIELD.EMAIL]: 'email',
  [PASSENGER_DATA_FIELD.PHONE]: 'phoneNumber',
  [PASSENGER_DATA_FIELD.DATE_OF_BIRTH]: 'birthDate',
  [PASSENGER_DATA_FIELD.GENDER]: 'gender',
};

export const PURCHASER_FIELD_NAME_MAP: Record<
  PURCHASER_DATA_FIELD,
  'firstName' | 'lastName' | 'email' | 'phoneNumber'
> = {
  [PURCHASER_DATA_FIELD.FIRST_NAME]: 'firstName',
  [PURCHASER_DATA_FIELD.LAST_NAME]: 'lastName',
  [PURCHASER_DATA_FIELD.EMAIL]: 'email',
  [PURCHASER_DATA_FIELD.PHONE]: 'phoneNumber',
};

const FIELD_NAME_MAP = {
  ...PASSENGER_FIELD_NAME_MAP,
  ...PURCHASER_FIELD_NAME_MAP,
};

export type PassengerFieldNames =
  (typeof PASSENGER_FIELD_NAME_MAP)[keyof typeof PASSENGER_FIELD_NAME_MAP];
export type PurchaserFieldNames =
  (typeof PURCHASER_FIELD_NAME_MAP)[keyof typeof PURCHASER_FIELD_NAME_MAP];

export interface PersonDataField {
  name: PassengerFieldNames | PurchaserFieldNames;
  isRequired: boolean;
}

type RequiredPersonDataField = {
  visibilityOptionType: { id: VISIBILITY_OPTION; name: string };
  dataField: { id: PASSENGER_DATA_FIELD | PURCHASER_DATA_FIELD; name: string };
};

const processFields = (
  fields: Array<PersonDataFieldDTO> = []
): Array<PersonDataField> => {
  return fields
    .filter((field): field is RequiredPersonDataField => {
      const visibilityId = field.visibilityOptionType?.id;
      const dataFieldId = field.dataField?.id;

      return (
        !!visibilityId &&
        (visibilityId === VISIBILITY_OPTION.DISPLAYED ||
          visibilityId === VISIBILITY_OPTION.MANDATORY) &&
        Object.values(VISIBILITY_OPTION).includes(visibilityId) &&
        !!dataFieldId &&
        dataFieldId in FIELD_NAME_MAP
      );
    })
    .map((field) => {
      const visibilityId = field.visibilityOptionType.id;
      const dataFieldId = field.dataField.id;
      const name = FIELD_NAME_MAP[dataFieldId];

      return {
        name,
        isRequired: visibilityId === VISIBILITY_OPTION.MANDATORY,
      };
    });
};

export const usePersonFieldRenderer = () => {
  const posConfiguration = useSelector(salesPointConfigurationSelector);

  return useMemo(
    () => ({
      passengerAdmissionFields: processFields(
        posConfiguration?.passengerAdmissionDataFields
      ),
      passengerTravelPassFields: processFields(
        posConfiguration?.passengerTravelPassDataFields
      ),
      purchaserFields: processFields(posConfiguration?.purchaserDataFields),
    }),
    [posConfiguration]
  );
};
